import SearchIconBtn from '../../assets/icons/SearchIconBtn'

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { z } from 'zod'
import { DeleteEntity } from '../../api/DeleteEntityMasterApi.js'
import { getEntityList } from '../../api/GetEntityMasterApi'
import SaveEntityMasterApi from '../../api/SaveEntityMasterApi'
import UpdateEntityMasterApi from '../../api/UpdateEntityMasterApi'
import { downloadEntityExcel } from '../../api/adminUser.js'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import { columns, searchFields } from '../../constants/entityColumn.js'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { DEFAULT_PAGE, getValidCriteria ,sanitizeForLog} from '../../constants/index.js'
import { CommonModal, CommonTable, ConfirmationDialog, Search } from '../common'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import { saveBlobAsFile } from "../../api/apiUtils.js"
import CustomInput from '../Returns/CustomInput'
import { clearEntityList } from '../../store/slice/entityTanSlice.js'

const ALLOWED_PAN_STATUS = ['A', 'B', 'C', 'F', 'G', 'H', 'J', 'L', 'P', 'T']

const entitySchema = z.object({
  entityName: z
    .string()
    .min(1, { message: 'Entity name is required' })
    .max(75, { message: 'Entity name cannot exceed 75 characters' })
    .regex(/^[A-Za-z0-9\s!@#$%&*()_\-+=\[\]{};:'",.<>/?\\|`~]+$/, {
      message:
        'Entity name can include letters, numbers, spaces, and special characters except caret (^)',
    }),
  entityPan: z
    .string()
    .min(1, { message: 'PAN is required' })
    .max(10, { message: 'PAN must be 10 characters' })
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, {
      message: 'Invalid PAN: Format must be 5 letters + 4 digits + 1 letter',
    })
    .refine(
      val =>
        val.length === 10 ? ALLOWED_PAN_STATUS.includes(val.charAt(3)) : true,
      {
        message: `4th character must be one of: ${ALLOWED_PAN_STATUS.join(
          ', '
        )}`,
      }
    )
})

const Entity = ({ roleConfig }) => {
  const dispatch = useDispatch()
  const [entities, setEntities] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingEntity, setEditingEntity] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [entityToDelete, setEntityToDelete] = useState(null)
  const [startDownloading, setStartDownloading] = useState(false)
  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)

  // Form state
  const [formData, setFormData] = useState({
    entityName: '',
    entityPan: '',
  })
  const [formErrors, setFormErrors] = useState({})
  const hasFetchedRef = useRef(false)
  const { showSnackbar } = useSnackbar();

  // Function to load entities from API
  const loadEntities = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList = []
  ) => {
    setLoading(true)
    const payload = {
      pageFilter: {
        currentPage: page,
        includeCount: true,
        pageSize: pageSize,
        sortField: 'lastModifiedDate',
        sortOrder: 'DESC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: searchList,
    }

    try {
      const response = await getEntityList(payload)
      if (response.success) {
        const responseData = response.data ? response.data.data.content : []
        setEntities(responseData || [])
        setTotalItems(response.data.data.totalElements)
      } else {
        console.error('Failed to load entities:')
        setEntities([])
      }
    } catch (error) {
      console.error('Error loading entities:')
      setEntities([])
    } finally {
      setLoading(false)
    }
  }

  // Load entities on component mount
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true
      loadEntities()
    }
  }, [])

  const validateForm = (dataToValidate = formData) => {
    const result = entitySchema.safeParse(dataToValidate)

    if (!result.success) {
      const zodErrors = {}
      result.error.issues.forEach(err => {
        if (err.path && err.path.length > 0) {
          zodErrors[err.path[0]] = err.message
        }
      })
      setFormErrors(zodErrors)
      return false
    }

    setFormErrors({})
    return true
  }

  // Validate individual field on change
  const validateField = (fieldName, value, currentFormData = formData) => {
    const testData = {
      ...currentFormData,
      [fieldName]: value
    }

    const result = entitySchema.safeParse(testData)

    if (!result.success) {
      const fieldError = result.error.issues.find(err =>
        err.path && err.path[0] === fieldName
      )

      if (fieldError) {
        setFormErrors(prev => ({
          ...prev,
          [fieldName]: fieldError.message
        }))
      } else {
        // Clear error if field is now valid
        setFormErrors(prev => ({
          ...prev,
          [fieldName]: ''
        }))
      }
    } else {
      // Clear error if validation passes
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }))
    }
  }

  const handleDownload = async () => {
    // Check if there's no data to download
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info');
      return;
    }

    setStartDownloading(true);

    try {
      const payload = {
        pageFilter: {
          currentPage,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'entityName',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: true,
        searchCOList: getValidCriteria(searchCriteria),
      };

      const blob = await downloadEntityExcel(payload);
      if (!blob) {
        showSnackbar('Failed to generate file', 'error');
        return;
      }

      // ✅ Veracode-safe download handler
      saveBlobAsFile(blob, 'entity.xlsx');
    } catch (error) {
      console.error('Failed to download Entity Excel:', sanitizeForLog(error));
      showSnackbar('File download failed', 'error');
    } finally {
      setStartDownloading(false);
    }
  };


  const handleAddNew = () => {
    setEditingEntity(null)
    setFormData({
      entityName: '',
      entityPan: '',
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const handleEdit = entity => {
    setEditingEntity(entity)
    setFormData({
      entityName: entity.entityName,
      entityPan: entity.entityPan,
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const handleDelete = entity => {
    setEntityToDelete(entity)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (entityToDelete) {
      try {
        await DeleteEntity(entityToDelete.uuid)
        dispatch(clearEntityList())
        setDeleteDialogOpen(false)
        setEntityToDelete(null)
        loadEntities(
          currentPage,
          itemsPerPage,
          getValidCriteria(searchCriteria)
        )
      } catch (error) {
        console.error('Failed to delete Entity Master:')
      }
    }
  }

  const handleInputChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value,
    }

    setFormData(newFormData)

    // Validate field on change
    validateField(field, value, newFormData)
  }

  const handleFormSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) return

    setFormLoading(true)

    const resetForm = () => {
      setFormData({ entityName: '', entityPan: '' })
      setFormErrors({})
      setEditingEntity(null)
      setModalOpen(false)
    }

    try {
      let apiResponse

      if (editingEntity) {
        // Update existing entity
        const updateData = {
          ...formData,
          uuid: editingEntity.uuid,
        }

        apiResponse = await UpdateEntityMasterApi(updateData)

        if (apiResponse.success) {
          dispatch(clearEntityList())
          showSnackbar('Entity updated successfully!', 'success');
          await loadEntities(currentPage, itemsPerPage, [])
        } else {
          throw new Error(apiResponse.error || 'Failed to update entity')
        }
      } else {
        // Save new entity
        apiResponse = await SaveEntityMasterApi(formData)

        if (apiResponse.success) {
          dispatch(clearEntityList())
          showSnackbar('Entity saved successfully!', 'success');
          await loadEntities(currentPage, itemsPerPage, [])
        } else {
          throw new Error(apiResponse.error || 'Failed to save entity')
        }
      }

      resetForm()
    } catch (error) {
      if (error instanceof z.ZodError) {
        const formErrors = {}
        error.issues.forEach(err => {
          formErrors[err.path[0]] = err.message
        })
        setFormErrors(formErrors)
      } else {
        const errMsg = error.message || 'An unexpected error occurred'
        setFormErrors({ general: errMsg })
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditingEntity(null)
    setFormData({
      entityName: '',
      entityPan: '',
    })
    setFormErrors({})
  }

  const handlePageChange = page => {
    setCurrentPage(page)
    loadEntities(page, itemsPerPage, getValidCriteria(searchCriteria))
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    loadEntities(1, newItemsPerPage, getValidCriteria(searchCriteria))
  }

  // search

  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await loadEntities(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadEntities(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  return (
    <Box>
      {/* Sub Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          Entity
        </Typography>

        {/* Right side - Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <span className="disabled:cursor-not-allowed">
              <IconButton
                onClick={handleDownload}
                disabled={loading || startDownloading}

              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton
              onClick={handleSearch}

            >
              <SearchIconBtn />
            </IconButton>
          </Tooltip>

          {roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              ></Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  backgroundColor: 'var( --forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                Add Entity
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Table */}
      <Box>
        <CommonTable
          columns={columns}
          data={entities}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          emptyMessage="No entities found"
          showFooter={true}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          showTotalItems={true}
          showPageNumbers={true}
          showActions={roleConfig.canEdit}
        />
      </Box>

      {/* Add/Edit Modal */}
      <CommonModal
        open={modalOpen}
        onClose={handleModalClose}
        title={editingEntity ? 'Edit Entity' : 'Add New Entity'}
        maxWidth="sm"
      >
        <>
          {/* Mandatory fields note */}
          <Typography
            variant="body2"
            sx={{
              color: 'var(--forbrand-error)',
              mb: 4,
              fontSize: '14px',
            }}
          >
            (*) Fields are mandatory
          </Typography>

          {/* Form container */}
          <Box>
            <form onSubmit={handleFormSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <CustomInput
                  label="Entity Name"
                  required
                  placeholder="Entity Name"
                  value={formData.entityName}
                  onChange={e =>
                    handleInputChange('entityName', e.target.value)
                  }
                  error={!!formErrors.entityName}
                  helperText={formErrors.entityName}
                  maxLength={75}
                />

                <CustomInput
                  label="Entity PAN"
                  required
                  placeholder="Entity PAN"
                  value={formData.entityPan}
                  onChange={e =>
                    handleInputChange('entityPan', e.target.value.toUpperCase())
                  }
                  error={!!formErrors.entityPan}
                  helperText={formErrors.entityPan}
                  maxLength={10}
                />
              </Box>
              {formErrors.general && (
                <Typography variant="body2" color="error" sx={{ my: 1 }}>
                  {formErrors.general}
                </Typography>
              )}
            </form>
          </Box>

          {/* Save button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: '32px',
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={formLoading}
              onClick={handleFormSubmit}
              sx={{
                backgroundColor: 'var(--forbrand-bg-variant)',
                color: 'var(--forbrand-brand-varient)',
                textTransform: 'none',
                px: '17px',
                py: '8px',
                fontSize: '16px',
                '&:disabled': {
                  backgroundColor: '#ccc',
                },
              }}
            >
              {formLoading ? 'Saving...' : editingEntity ? 'Update' : 'Save'}
            </Button>
          </Box>
        </>
      </CommonModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this entity?"
        message={`On deletion of entity, all the data pertaining to it would get delete. Do you want to continue ?`}
        confirmText="Delete"
        itemType="entity"
        type="delete"
      />

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </Box>
  )
}

export default Entity
